/** Sticky Save footer + optimistic-concurrency conflict banner for detail-page
 *  editor forms. Extracted from the old tours tab footer so the satellite catalog
 *  editors (regions, trip types, …) share one implementation (the tour record now
 *  saves through its Changes ledger).
 *
 *  The bar embeds the unsaved-changes navigation guard (`useBlocker` via
 *  useUnsavedChangesPrompt) — every editor rendering it is protected against
 *  losing edits to a stray click. Pages that own a multi-tab, page-level guard
 *  (itineraries) pass `guard={false}`, and then the bar registers NO blocker at
 *  all (see `NavigationGuard`). ⌘S / Ctrl+S saves when dirty.
 */
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { BAR_CONTROLS_SX, useBottomBarClearance } from '@/layout/AppScroller';

export function ConflictBanner({
  open,
  onReload,
  // NB: don't promise "edits stay" — most editors re-seed the form when the
  // reloaded record arrives (the reset-on-record-change effect).
  message = 'This record changed on the server (another editor). Reload before saving.',
  changedFields,
}: {
  open: boolean;
  onReload: () => void;
  message?: string;
  /** Names of the fields that drifted (see `diffFieldNames`) — turns the
   *  banner from a blind warning into a decision. */
  changedFields?: string[];
}) {
  if (!open) return null;
  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={<Button color="inherit" size="small" onClick={onReload}>Reload</Button>}
    >
      {message}
      {changedFields && changedFields.length > 0 && (
        <>
          {' '}
          Changed on the server: <strong>{changedFields.join(', ')}</strong>.
        </>
      )}
    </Alert>
  );
}

/** The bar's navigation guard, mounted only when the bar owns the guard.
 *
 *  A data router honours only its most recently registered blocker, and
 *  `useBlocker` registers one on mount whatever its `when` says. Calling the
 *  hook in the bar itself with `when=false` therefore still registered a
 *  blocker — and a bar mounting after the page (its tab renders once the record
 *  loads) displaced the page-level guard it was told to defer to, so leaving a
 *  dirty itinerary discarded the draft without a prompt. As a child component,
 *  `guard={false}` registers nothing and the bar's own hooks stay unconditional. */
function NavigationGuard({ when, message }: { when: boolean; message?: string }) {
  const { prompt } = useUnsavedChangesPrompt(when, message ? { message } : {});
  return prompt;
}

export function EditorSaveBar({
  dirty,
  saving,
  canManage,
  onSave,
  onDiscard,
  guard = true,
  guardMessage,
  blockReason,
}: {
  dirty: boolean;
  saving: boolean;
  canManage: boolean;
  onSave: () => void;
  /** Reverts the editor to the last-saved record (e.g. the same reset the
   *  tab runs after a refetch). Button hidden when omitted. */
  onDiscard?: () => void;
  /** Register the navigation guard (default true). `false` when the page owns
   *  a guard covering several mounted tabs — a router supports one blocker, so
   *  `false` mounts none here. */
  guard?: boolean;
  /** Resource-specific wording for the guard's confirm dialog. */
  guardMessage?: string;
  /** Why this draft cannot be saved yet (cross-field validation the server
   *  does not enforce) — disables Save and becomes its tooltip. Null/undefined
   *  means saveable. The draft stays dirty, so the navigation guard still
   *  protects it. */
  blockReason?: string | null;
}) {
  // ⌘S / Ctrl+S → save. Listener only exists while a save is possible; the
  // visibility check keeps a hidden bar (rare multi-editor pages) inert.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // `blockReason` gates the shortcut too — otherwise ⌘S walks straight past a
    // disabled Save button.
    if (!dirty || saving || !canManage || blockReason) return undefined;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (rootRef.current?.offsetParent != null) onSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dirty, saving, canManage, blockReason, onSave]);
  // Stuck over the scroller's bottom edge, the bar hides whatever focus scrolls to that
  // edge: it pads the scroller's focus scrolling by its height (see AppScroller).
  useBottomBarClearance(rootRef);

  const disabledReason = !canManage
    ? 'You do not have permission to edit this record'
    : !dirty
      ? 'No changes to save'
      : (blockReason ?? null);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'sticky',
        bottom: 0,
        // Lift the bar above the form content it floats over. MUI's InputLabel
        // is `position: absolute; z-index: 1`, so without a z-index here the
        // (positioned, z-index:auto) sticky bar paints *below* those labels and
        // they bleed through its opaque background as you scroll. 2 clears them.
        zIndex: 2,
        mt: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        ...BAR_CONTROLS_SX,
      }}
    >
      {guard && <NavigationGuard when={dirty && canManage} message={guardMessage} />}
      <Typography
        variant="body2"
        sx={{ flexGrow: 1 }}
        color={
          canManage && dirty && !saving ? (blockReason ? 'error.main' : 'warning.main') : 'text.secondary'
        }
      >
        {!canManage
          ? 'Read-only — you lack the manage permission'
          : saving
            ? 'Saving…'
            : dirty
              ? // Spell the blocker out here, not only in the button's tooltip:
                // a disabled Save you have to hover to understand is a dead end.
                (blockReason ?? 'Unsaved changes')
              : 'All changes saved'}
      </Typography>
      {onDiscard && canManage && (
        <Button onClick={onDiscard} disabled={!dirty || saving}>
          Discard
        </Button>
      )}
      <Tooltip title={disabledReason ?? ''}>
        <span>
          <Button variant="contained" disabled={disabledReason != null || saving} onClick={onSave}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
